// packages/components/src/agent_profile/showcase/FeatureList.tsx
import React, { useMemo } from 'react';
import { Badge, Tooltip } from '@dopomogai/ui';
import type { FeatureItem } from '@dopomogai/types';
import { Info, Beaker, Clock, Link as LinkIcon } from 'lucide-react';
import { TierBadge } from './TierBadge';

type Props = {
  features: FeatureItem[];
  // highlightKey is kept for back-compat but intentionally ignored (no flash/highlight)
  highlightKey?: string;
  groupOrderHint?: string[]; // optional ordering of categories
  className?: string;
};

const maturityAccent: Record<FeatureItem['maturity'], { label: string; color: 'green' | 'blue' | 'purple' | 'yellow' }> = {
  shipped_now: { label: 'Shipped', color: 'green' },
  '1w': { label: '1 week', color: 'blue' },
  '3w': { label: '3 weeks', color: 'purple' },
  research: { label: 'Research', color: 'yellow' },
};

function groupByCategory(features: FeatureItem[]) {
  const map: Record<string, FeatureItem[]> = {};
  for (const f of features) {
    map[f.category] = map[f.category] || [];
    map[f.category].push(f);
  }
  return map;
}

export function FeatureList({ features, groupOrderHint, className }: Props) {
  const grouped = useMemo(() => groupByCategory(features), [features]);

  const orderedCategories = useMemo(() => {
    const cats = Object.keys(grouped);
    if (!groupOrderHint || groupOrderHint.length === 0) return cats.sort();
    const set = new Set(cats);
    const inOrder = groupOrderHint.filter((c) => set.has(c));
    const remaining = cats.filter((c) => !groupOrderHint.includes(c)).sort();
    return [...inOrder, ...remaining];
  }, [grouped, groupOrderHint]);

  if (!features || features.length === 0) {
    return <p className="text-sm text-slate-500">No features in this bundle.</p>;
  }

  return (
    <div className={className}>
      {orderedCategories.map((cat) => {
        const items = grouped[cat] || [];
        return (
          <section key={cat} className="mb-5">
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{cat}</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {items.map((f) => {
                const maturity = maturityAccent[f.maturity];
                return (
                  <div
                    id={`feature-${f.key}`}
                    key={f.key}
                    className="bg-white border-2 border-black rounded-lg p-3 text-sm flex items-start gap-3"
                  >
                    <span className="mt-1 inline-block w-2 h-2 rounded-full bg-emerald-500" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-semibold text-slate-800">{f.title}</div>
                        <TierBadge tier={f.tier_gate as any} size="sm" />
                        <Badge text={maturity.label} accentColor={maturity.color} />
                      </div>
                      <div className="mt-1 flex items-center gap-3 text-xs text-slate-600 flex-wrap">
                        <span className="inline-flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {f.eta_days_from}–{f.eta_days_to} days
                        </span>
                        {Array.isArray(f.dependencies) && f.dependencies.length > 0 && (
                          <Tooltip text={`Requires: ${f.dependencies.join(', ')}`}>
                            <span className="inline-flex items-center gap-1 cursor-help">
                              <LinkIcon className="w-3.5 h-3.5" /> Needs {f.dependencies.length}
                            </span>
                          </Tooltip>
                        )}
                        <Tooltip text="Details coming soon">
                          <span className="inline-flex items-center gap-1 cursor-help">
                            <Info className="w-3.5 h-3.5" /> About
                          </span>
                        </Tooltip>
                        {f.maturity === 'research' && (
                          <span className="inline-flex items-center gap-1 text-amber-700">
                            <Beaker className="w-3.5 h-3.5" /> Experimental
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

export default FeatureList;
